import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const INVALID_LINK =
  "Bu şifre sıfırlama bağlantısı geçersiz veya süresi dolmuş. Yeni bir bağlantı talep edebilirsiniz.";

export default function AdminResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = (params.get("token") || "").trim();

  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const validatePw = (v) =>
    v.length >= 8 && /[A-Za-zÇĞİÖŞÜçğıöşü]/.test(v) && /\d/.test(v);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) { setError(INVALID_LINK); return; }
    if (pw !== pw2) { setError("Şifreler eşleşmiyor."); return; }
    if (!validatePw(pw)) {
      setError("Şifre en az 8 karakter, bir harf ve bir rakam içermelidir.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/admin/auth/reset-password", {
        token,
        new_password: pw,
        new_password_confirm: pw2,
      });
      toast.success("Şifreniz başarıyla güncellendi.");
      navigate("/admin/login", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : INVALID_LINK);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-6"
      data-testid="admin-reset-password-page"
    >
      <div className="w-full max-w-sm bg-white/[0.04] border border-white/10 rounded-2xl p-8 backdrop-blur">
        <div className="flex items-center gap-2 mb-6">
          <img src="/penz-icon.png" alt="PENZ" className="w-9 h-9 rounded-lg bg-white/10 p-1 object-contain" />
          <div>
            <div className="font-display font-semibold">PENZ Yönetici</div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-white/50">Yeni Şifre Belirle</div>
          </div>
        </div>

        <form onSubmit={onSubmit} className="space-y-4" data-testid="admin-reset-password-form">
          <div className="space-y-1.5">
            <Label className="text-xs text-white/70">Yeni Şifre</Label>
            <div className="relative">
              <Input
                type={show ? "text" : "password"}
                autoComplete="new-password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                className="h-10 bg-white/5 border-white/15 text-white rounded-lg pr-10"
                required
                minLength={8}
                data-testid="admin-reset-password-input"
              />
              <button
                type="button"
                onClick={() => setShow((v) => !v)}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-white/60 hover:text-white"
                aria-label={show ? "Şifreyi gizle" : "Şifreyi göster"}
                tabIndex={-1}
                data-testid="admin-reset-password-toggle"
              >
                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-[11px] text-white/50 mt-1">
              En az 8 karakter, bir harf ve bir rakam içermelidir.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-white/70">Yeni Şifre (tekrar)</Label>
            <Input
              type={show ? "text" : "password"}
              autoComplete="new-password"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              className="h-10 bg-white/5 border-white/15 text-white rounded-lg"
              required
              minLength={8}
              data-testid="admin-reset-password-confirm-input"
            />
          </div>

          {error && (
            <div className="text-xs px-3 py-2 rounded-lg bg-red-500/15 text-red-200 border border-red-500/30"
                 data-testid="admin-reset-password-error">
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-white text-slate-900 hover:bg-slate-100 h-10 rounded-lg"
            data-testid="admin-reset-password-submit-button"
          >
            {loading ? "Güncelleniyor..." : "Şifremi Güncelle"}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/admin/login"
            className="text-xs text-white/60 hover:text-white"
            data-testid="admin-reset-password-back-to-login"
          >
            Admin girişine dön
          </Link>
        </div>
      </div>
    </div>
  );
}
